// Dimension catalogue snapshot — bundled at build time.
//
// The Rule Book sub-panel (Governance §10.2) renders these
// definitions read-only. Bundling means the maintenance interface
// is one deploy behind the data files, which matches the deploy
// cadence (Pages and the data files are deployed together on every
// merge to main).
//
// If the dimension catalogue grows substantially, this module can
// switch to a runtime GET /api/dimensions endpoint with KV-backed
// reads. The current size (~7 KB total) is well below the threshold
// where bundling matters.

import pricingModels from '../../../data/dimensions/pricing-models.json';
import bureaus from '../../../data/dimensions/bureaus.json';
import bureauBandTables from '../../../data/dimensions/bureau-band-tables.json';
import housingStatuses from '../../../data/dimensions/housing-statuses.json';
import channels from '../../../data/dimensions/channels.json';
import gradeSubgradeTables from '../../../data/dimensions/grade-subgrade-tables.json';

export interface DimensionPricingModel {
  id: string;
  axes: string[];
  used_by_jurisdiction_products?: string[];
}

export interface DimensionBureau {
  id: string;
  name: string;
  country: string;
  score_scale_min: number;
  score_scale_max: number;
  source_url?: string;
  substantiation_doc?: string;
}

export interface DimensionBureauBand {
  id: string;
  label: string;
  min_score: number | null;
  max_score: number | null;
  _public_band: string;
  _floor_sentinel?: number;
}

export interface DimensionBureauBandTable {
  id: string;
  bureau_ref: string;
  _bureau_pending_confirmation?: boolean;
  source?: string;
  bands: DimensionBureauBand[];
}

export interface DimensionHousingStatus {
  id: string;
  label: string;
  _risk_ordinal: number;
}

export interface DimensionHousingTable {
  id: string;
  values: DimensionHousingStatus[];
}

export interface DimensionChannel {
  id: string;
  label: string;
  is_default_marketing: boolean;
  _risk_premium_min?: number;
  _loan_amount_max_override?: number;
  _substantiation_note?: string;
}

export interface DimensionChannelTable {
  id: string;
  values: DimensionChannel[];
}

export interface DimensionGradeRow {
  grade: string;
  subgrades: number[];
  _public_band: string;
  _risk_ordinal: number;
}

export interface DimensionGradeTable {
  id: string;
  source?: string;
  grades: DimensionGradeRow[];
  cell_id_format: string;
}

interface DimensionFile<T> {
  schema_version: string;
  models?: T[];
  bureaus?: T[];
  tables?: T[];
}

export const dimensions = {
  pricingModels: (pricingModels as DimensionFile<DimensionPricingModel>).models ?? [],
  bureaus: (bureaus as DimensionFile<DimensionBureau>).bureaus ?? [],
  bureauBandTables: (bureauBandTables as DimensionFile<DimensionBureauBandTable>).tables ?? [],
  housingStatuses: (housingStatuses as DimensionFile<DimensionHousingTable>).tables ?? [],
  channels: (channels as DimensionFile<DimensionChannelTable>).tables ?? [],
  gradeSubgradeTables: (gradeSubgradeTables as DimensionFile<DimensionGradeTable>).tables ?? []
};
